import { useCallback, useRef, useState } from 'react';

interface UseFormDraftTitleStateParams {
  draftTitle: string;
  setDraftTitle: (title: string) => void;
}

interface UseFormDraftTitleStateResult {
  isTitleDirty: boolean;
  seedDraftTitle: (source: string, title: string) => void;
  resetDraftTitleState: () => void;
}

/**
 * Shared draft-title lifecycle helper across form-editor surfaces.
 * Seeds once per source and tracks dirty state against first seeded value.
 */
export function useFormDraftTitleState({
  draftTitle,
  setDraftTitle,
}: UseFormDraftTitleStateParams): UseFormDraftTitleStateResult {
  const seededDraftTitleSourceRef = useRef<string | null>(null);
  const [initialDraftTitle, setInitialDraftTitle] = useState('');

  const seedDraftTitle = useCallback(
    (source: string, title: string) => {
      if (seededDraftTitleSourceRef.current === source) {
        return;
      }

      const nextTitle = title.trim();
      setDraftTitle(nextTitle);
      setInitialDraftTitle(nextTitle);
      seededDraftTitleSourceRef.current = source;
    },
    [setDraftTitle]
  );

  const resetDraftTitleState = useCallback(() => {
    setDraftTitle('');
    setInitialDraftTitle('');
    seededDraftTitleSourceRef.current = null;
  }, [setDraftTitle]);

  return {
    isTitleDirty: draftTitle.trim() !== initialDraftTitle.trim(),
    seedDraftTitle,
    resetDraftTitleState,
  };
}
